/**
 * PATH: packages/ui/src/kanban/components/card-detail/checklistItemRow.tsx
 * FILE_ID: UI-KANBAN-CARDDETAIL-CHECKLISTROW-001
 * PURPOSE: The recursive checklist-item row used by the card drawer's
 *          Checklists section — checkbox toggle, title, per-item DUE DATE,
 *          drag-to-nest, multi-select, delete. Extracted from checklists.tsx
 *          to keep that file under the 500-line working limit while adding
 *          the new per-item date control.
 * WHY:     Operator (2026-06-23): "Dates for action items to link up to the 8
 *          week look ahead." Each checklist step is an action item; giving it
 *          its OWN date (backend field already exists + is writable) is the
 *          foundation that later lets the weekly look-ahead bucket dated
 *          action items, not just card due dates.
 * FLOW:
 *   ┌──────────┐   ┌───────────────────────────┐   ┌────────────────────┐
 *   │ tree node│ → │ row (toggle/date/drag/del)│ → │ updateChecklistItem│
 *   └──────────┘   └───────────────────────────┘   └────────────────────┘
 * DEPENDENCIES:
 *   - ../../api KanbanApi: updateChecklistItem({ due_date }) / delete / move
 *   - ./checklistDates: pure due-pill formatter (tested)
 *   - @tanstack/react-query: per-row mutations invalidate via onChanged
 * RELATED:
 *   - card-detail/checklists.tsx: builds the tree + renders these rows.
 */

import { useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { CalendarPlus, X } from 'lucide-react'

import type { KanbanApi } from '../../api'
import type { CardChecklistItemT } from '../../types'
import { todayLocalYmd } from '../../utils/localDate'
import { useDismissOnOutsideInteraction } from '../../hooks/useDismissOnOutsideInteraction'
import { SmartDateInput } from '../DateField'
import {
  CHECKLIST_DUE_TONE_CLASS,
  formatChecklistDue,
} from './checklistDates'

/** Drag MIME for checklist-item reorder/nest (shared with checklists.tsx). */
export const CHECKLIST_ITEM_DRAG_MIME = 'text/x-checklist-item-id'

export interface ChecklistTreeNode {
  item: CardChecklistItemT
  children: ChecklistTreeNode[]
}

/**
 * Per-item due-date control. Collapsed: a compact pill when a date is set
 * (urgency-toned, struck/muted when the item is complete), or a faint
 * calendar affordance on hover when unset. Click reveals the shared
 * SmartDateInput (ARC-6 U05 — typed "tomorrow"/"week 6"/"12/8" + calendar
 * popover) whose commit PATCHes `due_date`; a clear button removes it.
 * Writes go through the same `updateChecklistItem` path the checkbox uses.
 */
function ChecklistItemDue({
  item,
  api,
  onChanged,
}: {
  item: CardChecklistItemT
  api: KanbanApi
  onChanged: () => void
}) {
  const [editing, setEditing] = useState(false)
  const editorRef = useRef<HTMLSpanElement>(null)

  // ARC-6: close the inline editor on outside interaction. The shared guard
  // in shouldDismissContextMenu keeps clicks inside the SmartDateInput's
  // portaled calendar (data-kanban-nested-popover) from counting as outside.
  useDismissOnOutsideInteraction(editorRef, () => setEditing(false), editing)

  const setDue = useMutation({
    mutationFn: (due: string | null) =>
      api.updateChecklistItem(item.id, { due_date: due }),
    onSuccess: () => {
      setEditing(false)
      onChanged()
    },
  })

  const display = formatChecklistDue(item.due_date, todayLocalYmd())

  if (editing) {
    // ARC-6 (U05): per-step date is the shared SmartDateInput — type
    // "tomorrow" / "week 6" / "12/8" instead of the strict native widget.
    return (
      <span
        ref={editorRef}
        className="inline-flex items-center gap-1"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setEditing(false)
        }}
      >
        <SmartDateInput
          ariaLabel="Step due date"
          placeholder="tomorrow · week 6 · 12/8"
          value={item.due_date?.slice(0, 10) ?? null}
          disabled={setDue.isPending}
          onChange={(iso) => {
            if (iso !== (item.due_date?.slice(0, 10) ?? null)) setDue.mutate(iso)
            else setEditing(false)
          }}
          className="w-[110px] rounded border border-blue-400 bg-white px-1 py-0.5 text-[10px] text-neutral-700 focus:outline-none focus:ring-1 focus:ring-blue-500/30 dark:bg-neutral-900 dark:text-neutral-200"
        />
      </span>
    )
  }

  if (display) {
    // Completed items mute their (now moot) date so a done step never
    // screams "Overdue".
    const toneClass = item.complete
      ? CHECKLIST_DUE_TONE_CLASS.future
      : CHECKLIST_DUE_TONE_CLASS[display.tone]
    return (
      <span className="inline-flex items-center gap-0.5">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            setEditing(true)
          }}
          title="Change due date"
          className={[
            'inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium leading-none ring-1',
            item.complete ? 'line-through opacity-70' : '',
            toneClass,
          ].join(' ')}
        >
          {display.label}
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            setDue.mutate(null)
          }}
          disabled={setDue.isPending}
          aria-label="Clear due date"
          title="Clear due date"
          className="rounded p-0.5 text-neutral-400 opacity-0 transition-opacity hover:bg-neutral-100 hover:text-neutral-600 group-hover/cli:opacity-100 dark:hover:bg-neutral-800"
        >
          <X className="h-2.5 w-2.5" />
        </button>
      </span>
    )
  }

  // No date yet — quiet calendar affordance, revealed on row hover.
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation()
        setEditing(true)
      }}
      aria-label="Add due date"
      title="Add due date"
      className="rounded p-0.5 text-neutral-400 opacity-0 transition-opacity hover:bg-neutral-100 hover:text-blue-600 group-hover/cli:opacity-100 dark:hover:bg-neutral-800"
    >
      <CalendarPlus className="h-3 w-3" />
    </button>
  )
}

/** Inner row — recursive. Renders one item then walks `node.children` with
 *  one indent step per `depth`. Indent step = 18px (matches the
 *  KANBAN_DESIGN_SYSTEM `indent_unit_px`). */
export function ChecklistItemRow({
  node,
  api,
  siblings,
  onChanged,
  onContextMenu,
  selectedIds,
  onToggleSelect,
}: {
  node: ChecklistTreeNode
  api: KanbanApi
  siblings: CardChecklistItemT[]
  onChanged: () => void
  onContextMenu: (item: CardChecklistItemT, ev: React.MouseEvent) => void
  selectedIds: Set<string>
  onToggleSelect: (id: string, shiftKey: boolean) => void
}) {
  const { item, children } = node
  const indentPx = (item.depth ?? 0) * 18
  const isSelected = selectedIds.has(item.id)

  const toggle = useMutation({
    mutationFn: () =>
      api.updateChecklistItem(item.id, { complete: !item.complete }),
    onSuccess: onChanged,
  })

  const removeItem = useMutation({
    mutationFn: () => api.deleteChecklistItem(item.id),
    onSuccess: onChanged,
  })

  // Drop-onto-row (NEST). Distinct visual feedback while dragging.
  const [isDropTarget, setIsDropTarget] = useState(false)
  const moveMutation = useMutation({
    mutationFn: (vars: { id: string; parent_id: string | null }) =>
      api.moveChecklistItem(vars.id, { parent_id: vars.parent_id }),
    onSuccess: onChanged,
  })

  return (
    <>
      <li
        className={[
          'group/cli flex items-start gap-1.5 rounded px-1 py-0.5 hover:bg-neutral-50 dark:hover:bg-neutral-900',
          isSelected ? 'bg-blue-50 dark:bg-blue-950/30' : '',
          isDropTarget ? 'ring-1 ring-blue-400 bg-blue-50/40 dark:bg-blue-950/40' : '',
        ].join(' ')}
        style={{ paddingLeft: 4 + indentPx }}
        draggable
        onDragStart={(e) => {
          e.dataTransfer.setData(CHECKLIST_ITEM_DRAG_MIME, item.id)
          e.dataTransfer.effectAllowed = 'move'
        }}
        onDragOver={(e) => {
          if (!e.dataTransfer.types.includes(CHECKLIST_ITEM_DRAG_MIME)) return
          e.preventDefault()
          e.dataTransfer.dropEffect = 'move'
          if (!isDropTarget) setIsDropTarget(true)
        }}
        onDragLeave={() => setIsDropTarget(false)}
        onDrop={(e) => {
          setIsDropTarget(false)
          const draggedId = e.dataTransfer.getData(CHECKLIST_ITEM_DRAG_MIME)
          if (!draggedId || draggedId === item.id) return
          // Same-checklist invariant is enforced server-side
          // (cross_checklist_move 400). Drop on this row = nest dragged
          // under this row.
          e.preventDefault()
          moveMutation.mutate({ id: draggedId, parent_id: item.id })
        }}
        onContextMenu={(e) => {
          e.preventDefault()
          onContextMenu(item, e)
        }}
        data-checklist-item-id={item.id}
        data-checklist-item-depth={item.depth ?? 0}
      >
        <input
          type="checkbox"
          checked={item.complete}
          onChange={() => toggle.mutate()}
          disabled={toggle.isPending}
          className="mt-0.5 h-3 w-3 cursor-pointer accent-emerald-600 shrink-0"
        />
        {/* Click on title body toggles multi-select; Shift+click extends. */}
        <span
          onClick={(e) => {
            onToggleSelect(item.id, e.shiftKey)
          }}
          className={[
            'flex-1 text-xs cursor-pointer select-none',
            item.complete
              ? 'line-through text-neutral-400'
              : 'text-neutral-700 dark:text-neutral-200',
          ].join(' ')}
        >
          {item.title}
        </span>
        {/* Per-item due date — the "dates for action items" control. Sits
            between the title and the delete affordance. */}
        <ChecklistItemDue item={item} api={api} onChanged={onChanged} />
        <button
          type="button"
          onClick={() => removeItem.mutate()}
          aria-label="Remove item"
          className="opacity-0 group-hover/cli:opacity-100 rounded p-0.5 text-neutral-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/30"
        >
          <X className="h-3 w-3" />
        </button>
      </li>
      {children.map((child) => (
        <ChecklistItemRow
          key={child.item.id}
          node={child}
          api={api}
          siblings={siblings}
          onChanged={onChanged}
          onContextMenu={onContextMenu}
          selectedIds={selectedIds}
          onToggleSelect={onToggleSelect}
        />
      ))}
    </>
  )
}
